import { useMemo, useRef, useEffect, useState } from 'react';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useEventInfo } from '@shared/utils/eventInfo';
import type { Maybe } from '@graphql/generated-base';
import { ScoringPriorityField } from '@graphql/generated-base';
import { addError } from '@shared/utils/logger';
import { useAutoPopulationApiV5 } from './api/useAutoPopulationApiV5';
import { useAutoPopulationPolling } from './hooks/useAutoPopulationPolling';
import { useAutoPopulationFeatureFlags } from './hooks/useAutoPopulationFeatureFlags';
import type { UseAutoPopulationOptions, UseAutoPopulationReturn, StartAutoPopulationParams } from './types';

const OPERATION_NAME = 'useAutoPopulation';

// Default search radius in kilometers for accommodation auto-population
const AUTO_POPULATE_DEFAULT_RADIUS_KM = 5;

// Default fields used to score and prioritize accommodations during auto-population
const DEFAULT_SCORING_PRIORITY_FIELDS = [
  ScoringPriorityField.standardRoomPriceDiscountPercent,
  ScoringPriorityField.userRating,
  ScoringPriorityField.distanceFromSearchLocation,
] as const;

/**
 * Hook for async (v5) auto-population of accommodations.
 *
 * This hook automatically populates accommodations near a given location by calling the v5 API,
 * which initiates an async job that is polled for completion.
 *
 * @returns Hook return value containing start function, status, results, and helper methods
 */
export const useAutoPopulation = (options: UseAutoPopulationOptions): UseAutoPopulationReturn => {
  const { mode = 'auto', onComplete, context, validateIsEnoughTimeElapsed, skipPolling = false } = options;

  const [isStarting, setIsStarting] = useState(false);

  const { eventInfo } = useEventInfo();
  const eventId = eventInfo?.eventId;

  // Prevents duplicate onComplete callbacks when polling updates and effects trigger simultaneously
  const hasCompletedRef = useRef(false);
  const currentEventIdRef = useRef<Maybe<string>>(eventId ?? null);

  // Feature flags control polling behavior
  const { hasEnoughTimeElapsed, isAutoPopulationDoneFromStorage, isLoadingFeatureFlags } = useAutoPopulationFeatureFlags(validateIsEnoughTimeElapsed);

  const { callAutoPopulationApiV5, loading: loadingV5 } = useAutoPopulationApiV5();

  // Skip polling when: time validation fails, mode is start-only, or no eventId
  const shouldSkipPolling = (validateIsEnoughTimeElapsed && !hasEnoughTimeElapsed) || mode === 'start-only' || !eventId;

  // Polling hook monitors v5 async job status and fetches results when complete
  const {
    status: pollStatus,
    results: pollResults,
    loading: loadingPoll,
    restartPolling,
  } = useAutoPopulationPolling({
    eventId,
    shouldSkipAutoPopulationCheck: shouldSkipPolling,
    context,
    skipPolling,
  });

  // Track previous poll status to detect PENDING → COMPLETED transitions
  const prevPollStatusRef = useRef(pollStatus);

  // Reset state when event changes to prevent stale data
  useEffect(() => {
    const newEventId = eventId ?? null;
    if (currentEventIdRef.current !== newEventId) {
      currentEventIdRef.current = newEventId;
      hasCompletedRef.current = false;
      setIsStarting(false);
    }
  }, [eventId]);

  // Trigger onComplete callback when polling transitions from PENDING to COMPLETED
  useEffect(() => {
    // Re-arm completion when a new polling cycle starts poll-only mode.
    if (hasCompletedRef.current && pollStatus === 'PENDING') {
      hasCompletedRef.current = false;
    }

    if (hasCompletedRef.current || mode === 'start-only' || isStarting) {
      prevPollStatusRef.current = pollStatus;
      return;
    }

    // Only fire onComplete when transitioning from PENDING → COMPLETED:
    // - Prevents duplicate side effects on remounts/refreshes (e.g., toasts, analytics)
    // - onComplete semantically means "job just finished", not "data is in completed state"
    const wasPending = prevPollStatusRef.current === 'PENDING';
    prevPollStatusRef.current = pollStatus;

    if (wasPending && pollStatus === 'COMPLETED' && pollResults.length > 0 && onComplete) {
      hasCompletedRef.current = true;
      onComplete(pollResults);
    }
  }, [pollStatus, pollResults, mode, onComplete, isStarting]);

  // Combine loading states from API, polling, and feature flags
  const isLoading = loadingV5 || loadingPoll || isLoadingFeatureFlags;

  // Derive unified status
  const status = useMemo(() => {
    if (isLoading || isStarting) return 'PENDING';
    if (shouldSkipPolling && mode === 'poll-only') return 'COMPLETED';
    return pollStatus;
  }, [isLoading, isStarting, pollStatus, shouldSkipPolling, mode]);

  const isCompletedFromApi = status === 'COMPLETED';

  // Start auto-population: validates params, calls v5 API, handles results
  const startAutoPopulation = useEventCallback(async (params: StartAutoPopulationParams) => {
    if (isStarting) return;

    const targetEventId = params.eventId ?? eventId;
    if (!targetEventId) {
      addError(new Error('Missing event id'), { context, operation: OPERATION_NAME });
      return;
    }

    const { venue, searchRadiusKm = AUTO_POPULATE_DEFAULT_RADIUS_KM, scoringPriorityFields = DEFAULT_SCORING_PRIORITY_FIELDS, scenario } = params;

    if (typeof venue.latitude !== 'number' || typeof venue.longitude !== 'number') {
      addError(new Error('Missing location'), { context, eventId: targetEventId, operation: OPERATION_NAME });
      return;
    }

    setIsStarting(true);
    hasCompletedRef.current = false;

    try {
      const v5Response = await callAutoPopulationApiV5({
        eventId: targetEventId,
        location: { latitude: venue.latitude, longitude: venue.longitude },
        scoringPriorityFields,
        context,
        searchRadiusKm,
        venueGooglePlaceId: venue.googlePlaceId ?? undefined,
        scenario,
      });

      if (v5Response?.error) {
        addError(new Error(v5Response.error), { context, eventId: targetEventId, isAsync: true, operation: 'v5Response' });
        return;
      }

      if (mode === 'auto') {
        restartPolling();
      }
    } catch (error) {
      addError(error instanceof Error ? error : new Error('Auto-population failed'), { context, eventId: targetEventId, operation: OPERATION_NAME });
    } finally {
      setIsStarting(false);
    }
  });

  // Manual completion handler for legacy code compatibility
  const handleOnComplete = useEventCallback(() => {
    if (hasCompletedRef.current) return;
    if (isCompletedFromApi && pollResults.length > 0) {
      hasCompletedRef.current = true;
      onComplete?.(pollResults);
    }
  });

  // Determine if auto-population is completed (combines API status with localStorage)
  // Used to prevent re-population when location changes if already completed
  const isAutoPopulationDone = useMemo(() => {
    if (isLoadingFeatureFlags) {
      return isAutoPopulationDoneFromStorage;
    }

    // If no operations found, fallback to localStorage
    if (status === 'IDLE' || (status === 'COMPLETED' && pollResults.length === 0)) {
      return isAutoPopulationDoneFromStorage;
    }

    // Check both API completion and localStorage for backwards compatibility
    return (isCompletedFromApi && pollResults.length > 0) || isAutoPopulationDoneFromStorage;
  }, [isLoadingFeatureFlags, status, isCompletedFromApi, pollResults.length, isAutoPopulationDoneFromStorage]);

  return {
    startAutoPopulation,
    onComplete: handleOnComplete,
    status,
    isAutoPopulationDone,
    results: pollResults,
  };
};
